import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { RootState } from '@root/rootReducer';

import { LayoutConfig } from 'config/layoutConfig';
import { Series } from '@models/Series';
import {
  AnalysisDefault,
  AnalysisType,
  PresetType,
  SelectedAnalysis,
  SelectedTool,
  SwitchConfigType,
  VOI,
} from 'config/analysisConfig';
import {
  globalToolStateV2,
  panelToolStateV2,
  resetLayoutConfig,
  resetSelectedSeriesLayout,
  resetToolStatesV2,
  setActivatedTool,
  setCurrentLayout,
  setLayoutStateLoading,
  setLayoutStateUpdateData,
  // setRecentlyUsedTool,
  setSelectedAnalysis,
  setSelectedTool,
  updateAnalysisDefault0,
  // setSynchPanelComponentKey,
  updateGlobalTool,
  updatePanelTool,
  updateSwitchConfig,
  updateSwitchConfigTarget,
  updateUIConfigPreset,
  updateUIConfigVOIInvert,
  updateUIConfigVOIWindowLevel,
  updateUIConfigVOIWindowLevelWidth,
  updateUIConfigVOIWindowWidth,
} from './AnalysisPageSlice';
import { PanelComponentKeyType, PanelType } from 'config/panelConfig';
import {
  currentGlobalToolStates,
  getSelectedAnalysisDefault,
  selectActivatedTool,
  selectCurrentPanelTool,
  selectExclusiveTool,
  // selectRecentlyUsedTool,
  selectSelectedTool,
  selectedPanelType,
} from './AnalysisPageSelectors';
import { ToolDefinition, ToolId } from 'config/toolDefinitionsConfig';
import { TOOL_REGISTRY } from 'config/toolRegistry';

export interface AnalysisPageHookResult {
  layoutConfig: LayoutConfig[];
  switchConfigs: SwitchConfigType;
  selectedLayoutConfig: LayoutConfig;

  selectedAnalysis: SelectedAnalysis | null;
  selectedAnalysisDefault?: AnalysisDefault;
  selectedPanel?: PanelType;
  selectedType: string;

  selectedSeriesId: string|undefined;

  currentVOI: VOI
  currentPreset: Record<string,PresetType>;
  // currentWindowLevel: number;
  // currentWindowWidth: number;

  selectedTool: SelectedTool|null;
  exclusiveTool: SelectedTool;
  activatedTools: Record<string,boolean>;
  // recentlyUsedTool: Record<ToolGroupType, string>;

  isPreview : boolean;
  isLayoutStateLoading: boolean;
  isLayoutStateUpdateData: boolean;
  // syncPanelComponentKey? : PanelComponentKeyType;
  updateCurrentLayout: (type: string) => void;
  updateSelectAnalysis: (series: Series, analysisType?: AnalysisType) => void;
  updateSelectedTool: (tool: SelectedTool) => void;
  updateActivatedTool: (tool: SelectedTool,check: boolean) => void;
  // updateRecentlyUsedTool: (groupName: ToolGroupType, id: string)=>void;
  // updateSyncCTPanelsToSelected: (componentKey? : PanelComponentKeyType) => void;
  updateSwitchConfigs : (config : Partial<SwitchConfigType>,componentKey? : PanelComponentKeyType)=>void;
  updateWindowLevel : (windowLevel : number)=>void;
  updateWindowWidth : (windowWidth : number)=>void;
  updateWindowLevelWidth : ([windowLevel,windowWidth] : [number,number])=>void;
  updateVOIInvert : (invert : boolean)=>void;
  updatePreset : (preset : Record<string,PresetType>)=>void;
  updateAnalysisDefault:  (analysisDefault: Partial<Record<AnalysisType,AnalysisDefault>>)=>void;
  handleResetLayoutConfig : () => void;
  handleLayoutStateLoading:(b:boolean)=>void;
  handleLayoutStateUpdateData:(b:boolean)=>void;
  panelId?: PanelType,
  panelToolState?: panelToolStateV2,
  globalToolState?: globalToolStateV2,
  handleUpdatePanelTool: (tool : ToolId) => void,
  handleUpdateGlobalTool: (tool : ToolId) => void,
  handleUpdateToolForPanel: (panelId : PanelType, id : ToolId) => void,
  handleResetToolState: () => void,
  resetSelectedSeries: () => void,
  // updateLoading:(flag:boolean)=>void;
}

export const useAnalysisPage = (): AnalysisPageHookResult => {
  const dispatch = useDispatch();

  const layoutConfig = useSelector((state: RootState) => state.analysis.uiConfig.layoutState.layoutConfig,shallowEqual);
  const switchConfigs = useSelector((state: RootState) => state.analysis.uiConfig.layoutState.switchConfigs,shallowEqual);
  const selectedLayoutConfig = useSelector((state: RootState) => state.analysis.uiConfig.selectedLayoutConfig,shallowEqual);

  const selectedAnalysis = useSelector((state:RootState) =>state.analysis.selectedAnalysis,shallowEqual);
  const selectedAnalysisDefault = useSelector(getSelectedAnalysisDefault,shallowEqual)
  const selectedPanel = useSelector((state: RootState) => state.analysis.uiConfig.layoutState.selectedPanelConfig,shallowEqual)?.panelId;
  // const selectedPanelType = useSelector((state: RootState) => state.analysis.uiConfig.layoutState.selectedPanelConfig,shallowEqual)?.componentKey;
  const selectedType = selectedLayoutConfig.type as string;

  const selectedSeriesId = useSelector((state: RootState) => state.analysis.selectedAnalysis?.seriesId, shallowEqual);

  const currentVOI = useSelector((state: RootState) => state.analysis.uiConfig.voi,shallowEqual);
  const currentPreset = useSelector((state: RootState) => state.analysis.uiConfig.preset,shallowEqual);
  // const currentWindowWidth = useSelector((state: RootState) => state.analysis.uiConfig.voi,shallowEqual);

  // toolStatesV2
  const panelToolState = useSelector(selectCurrentPanelTool, shallowEqual);
  const globalToolState = useSelector(currentGlobalToolStates, shallowEqual);
  const panelId = useSelector(selectedPanelType, shallowEqual);

  const selectedTool = useSelector(selectSelectedTool, shallowEqual);
  const exclusiveTool = useSelector(selectExclusiveTool, shallowEqual);
  const activatedTools = useSelector(selectActivatedTool, shallowEqual);
  // const recentlyUsedTool = useSelector(selectRecentlyUsedTool, shallowEqual);

  const isPreview = useSelector((state:RootState) =>state.analysis.selectedAnalysis?.analysisType==='preview',shallowEqual)
  const isLayoutStateLoading = useSelector((state:RootState)=>state.analysis.uiConfig.layoutState.loading,shallowEqual);
  const isLayoutStateUpdateData = useSelector((state:RootState)=>state.analysis.uiConfig.layoutState.updateData,shallowEqual);
  // const syncPanelComponentKey=useSelector((state:RootState)=>state.analysis.uiConfig.layoutState.syncPanelComponentKey,shallowEqual);

  const updateCurrentLayout = useCallback(
    (type: string) => dispatch(setCurrentLayout(type)),
    [dispatch],
  );

  const updateSelectAnalysis = useCallback(
    (series: Series, analysisType?: AnalysisType) =>
      dispatch(
        setSelectedAnalysis({
          seriesId: series.id,
          analysisType,
        }),
      ),
    [dispatch],
  );
  // 선택된 패널에 툴 넣기
  const handleUpdatePanelTool = useCallback(
    (id: ToolId) => {
      if (!panelId) return;
      const toolDef: ToolDefinition | undefined = TOOL_REGISTRY.find(
        (t: ToolDefinition) => t.id === id
      );
      if (toolDef?.behavior === 'exclusive') {
        dispatch(updatePanelTool({ panel: panelId, id, group: toolDef.group }));
      }
    },
    [dispatch, panelId]
  );
  // 파라미터로 받은 패널에 툴 넣기
  const handleUpdateToolForPanel = useCallback(
    (panelId: PanelType, id: ToolId) => {
      const toolDef = TOOL_REGISTRY.find(
        (t: ToolDefinition) => t.id === id
      );

      if (toolDef?.behavior === 'exclusive') {
        dispatch(updatePanelTool({ panel: panelId, id, group: toolDef.group }));
      }
    },
    [dispatch]
  )
  const handleResetToolState = useCallback(
    () => {
      dispatch(resetToolStatesV2());
    },
    [dispatch]
  )

  const handleUpdateGlobalTool = useCallback(
    (id: ToolId) => {
      const toolDef: ToolDefinition | undefined = TOOL_REGISTRY.find(
        (t: ToolDefinition) => t.id === id
      );
      if (toolDef?.behavior === 'toggle') {
        dispatch(updateGlobalTool(id));
      }
    },
    [dispatch]
  );

  const updateSelectedTool = useCallback(
    (tool: SelectedTool) => dispatch(setSelectedTool(tool)),
    [dispatch],
  );

  //이것은 업데이트가 아니라 set
  const updateActivatedTool = useCallback((tool:SelectedTool,check: boolean)=>dispatch(setActivatedTool({tool,check})),[dispatch])

  // const updateRecentlyUsedTool = useCallback(
  //   (groupName: ToolGroupType, id: string)=>dispatch(setRecentlyUsedTool([groupName,id])),[dispatch]
  // )
  // const updateLoading = useCallback((flag:boolean)=>dispatch(setLoading(flag)),[dispatch]);


  const updateSwitchConfigs = useCallback(
    // (switchKey : SwitchConfigs, flag: boolean)=>{
    (switchConfig : Partial<SwitchConfigType>,
      componentKey?: PanelComponentKeyType
    )=>{
      if(!componentKey) dispatch(updateSwitchConfig(switchConfig));
      else dispatch(updateSwitchConfigTarget([switchConfig,componentKey]))
    },[dispatch])

  const updateWindowLevel = useCallback((windowLevel : number)=>{
    dispatch(updateUIConfigVOIWindowLevel(windowLevel));
  },[dispatch])
  const updateWindowWidth = useCallback((windowWidth : number)=>{
    dispatch(updateUIConfigVOIWindowWidth(windowWidth));
  },[dispatch])
  const updateWindowLevelWidth = useCallback(([windowLevel,windowWidth]:[number,number])=>{
    dispatch(updateUIConfigVOIWindowLevelWidth([windowLevel,windowWidth]));
  },[dispatch])
  const updateVOIInvert = useCallback((invert : boolean)=>{
    dispatch(updateUIConfigVOIInvert(invert));
  },[dispatch])
  const updatePreset = useCallback((preset : Record<string,PresetType>)=>{
    dispatch(updateUIConfigPreset(preset));
  },[dispatch])
  // const updateSyncCTPanelsToSelected = useCallback((componentKey? : PanelComponentKeyType) => {
  //   dispatch(setSynchPanelComponentKey(componentKey));
  // }, [dispatch]);
  const updateAnalysisDefault = useCallback((analysisDefault: Partial<Record<AnalysisType,AnalysisDefault>>)=>{
    dispatch(updateAnalysisDefault0(analysisDefault));
  },[dispatch])

  const handleResetLayoutConfig = useCallback(() =>
    dispatch(resetLayoutConfig())
  ,[dispatch])

  const handleLayoutStateLoading = useCallback((b : boolean)=>{

    dispatch(setLayoutStateLoading(b));
  },[dispatch])
  const handleLayoutStateUpdateData = useCallback((b : boolean)=>{
    dispatch(setLayoutStateUpdateData(b));
  },[dispatch])

  const resetSelectedSeries = useCallback(()=>{
    dispatch(resetSelectedSeriesLayout());
  },[dispatch])
  return useMemo(
    () => ({
      layoutConfig,
      switchConfigs,
      selectedAnalysis,

      selectedAnalysisDefault,
      selectedLayoutConfig,
      selectedPanel,
      // selectedPanelType,
      selectedType,

      selectedSeriesId,

      currentVOI,
      currentPreset,
      // currentWindowLevel,
      // currentWindowWidth,
      // panelStates,

      // selectedStudyTitle,
      // topLeftInfo,
      // topRightInfo,
      // CTBottomRightSubInfo,

      selectedTool,
      exclusiveTool,
      activatedTools,
      // recentlyUsedTool,

      isPreview,
      isLayoutStateLoading,
      isLayoutStateUpdateData,
      // syncPanelComponentKey,
      updateCurrentLayout,
      updateSelectAnalysis,
      updateSelectedTool,
      updateActivatedTool,
      // updateRecentlyUsedTool,
      // updateSyncCTPanelsToSelected, // 동기화 상태 업데이트를 위해 추가함 - 박정진
      updateSwitchConfigs,
      updateWindowLevel,
      updateWindowWidth,
      updateWindowLevelWidth,
      updateVOIInvert,
      updatePreset,
      updateAnalysisDefault,
      handleResetLayoutConfig,
      handleLayoutStateLoading,
      handleLayoutStateUpdateData,
      // updateLoading,
      panelId,
      panelToolState,
      globalToolState,
      handleUpdatePanelTool,
      handleUpdateGlobalTool,
      handleUpdateToolForPanel,
      handleResetToolState,
      resetSelectedSeries
    }),
    [
      layoutConfig,
      switchConfigs,
      selectedAnalysis,

      selectedAnalysisDefault,
      selectedLayoutConfig,
      selectedPanel,
      // selectedPanelType,
      selectedType,

      selectedSeriesId,

      currentVOI,
      currentPreset,
      // currentWindowLevel,
      // currentWindowWidth,
      // panelStates,

      // selectedStudyTitle,
      // topLeftInfo,
      // topRightInfo,
      // CTBottomRightSubInfo,

      selectedTool,
      exclusiveTool,
      activatedTools,
      // recentlyUsedTool,

      isPreview,
      isLayoutStateLoading,
      isLayoutStateUpdateData,
      // syncPanelComponentKey,
      updateCurrentLayout,
      updateSelectAnalysis,
      updateSelectedTool,
      updateActivatedTool,
      // updateRecentlyUsedTool,
      // updateSyncCTPanelsToSelected,  // 동기화 상태 업데이트를 위해 추가함 - 박정진
      updateSwitchConfigs,
      updateWindowLevel,
      updateWindowWidth,
      updateWindowLevelWidth,
      updateVOIInvert,
      updatePreset,
      updateAnalysisDefault,
      handleResetLayoutConfig,
      handleLayoutStateLoading,
      handleLayoutStateUpdateData,
      // updateLoading,
      panelId,
      panelToolState,
      globalToolState,
      handleUpdatePanelTool,
      handleUpdateGlobalTool,
      handleUpdateToolForPanel,
      handleResetToolState,
      resetSelectedSeries
    ],
  );
};
